import { storeToRefs } from "pinia";
import RestService from "~/core/services/rest.service";
import organizationService from "~/modules/organization/services/organization.service";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorAssignationsEntityType } from "~/modules/sector/models/SectorAssignationsEntityType.enum";
import type { SectorAttachDetachPayload } from "~/modules/sector/models/SectorAttachDetachPayload.interface";
import type { SectorCreatePayload } from "~/modules/sector/models/SectorCreatePayload.interface";
import type { SectorCreateUpdateResponse } from "~/modules/sector/models/SectorCreateUpdateResponse.type";
import type { SectorUpdatePayload } from "~/modules/sector/models/SectorUpdatePayload.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";

export class SectorService extends RestService {
  /**
   * Retrieve sector with his parents
   * @param organizationCode
   * @param sectorCode
   * @param showStatusesDeletable
   */
  getSectorByCode(
    organizationCode: number,
    sectorCode: number,
    showStatusesDeletable?: boolean,
  ): Promise<Sector> {
    return this.$apiV2.get(
      `${this.getOrganizationSectorsPath(organizationCode)}/${sectorCode}`,
      { params: { showStatusesDeletable } },
    );
  }

  /**
   * Retrieves sectors by codes
   * @param codes
   */
  getSectorsByCodes(codes: string[]): Sector[] {
    const { availableSectors } = storeToRefs(useSectorStore());
    return availableSectors.value.filter((sector) =>
      codes.includes(sector.code.toString()),
    );
  }

  getSectors({
    organizationCode,
    searchParams,
  }: {
    organizationCode?: number;
    searchParams?: string;
  }): Promise<Sector> {
    const params: { search?: string } = {};

    if (searchParams) {
      params.search = searchParams;
    }

    return this.$apiV2.get(this.getOrganizationSectorsPath(organizationCode), {
      params,
    });
  }

  /**
   * Create a sector
   * @param organizationCode
   * @param sector
   */
  createSector(
    organizationCode: number,
    sector: SectorCreatePayload,
  ): Promise<SectorCreateUpdateResponse> {
    return this.$apiV2.post(
      this.getOrganizationSectorsPath(organizationCode),
      sector,
    );
  }

  /**
   * Update a sector
   * @param organizationCode
   * @param sectorCode
   * @param sector
   */
  updateSector(
    organizationCode: number,
    sectorCode: number,
    sector: SectorUpdatePayload,
  ): Promise<SectorCreateUpdateResponse> {
    return this.$apiV2.put(
      `${this.getOrganizationSectorsPath(organizationCode)}/${sectorCode}`,
      sector,
    );
  }

  /**
   * Remove a sector
   * @param organizationCode
   * @param sectorCode
   */
  removeSector(organizationCode: number, sectorCode: number) {
    return this.$apiV2.delete(
      `${this.getOrganizationSectorsPath(organizationCode)}/${sectorCode}`,
    );
  }

  attachDetachEntitiesToSector(
    organizationCode: number,
    sectorCode: number,
    attachDetachPayload: SectorAttachDetachPayload,
    entityType: SectorAssignationsEntityType,
  ): Promise<void> {
    return this.$apiV2.patch(
      `${this.getOrganizationSectorsPath(organizationCode)}/${sectorCode}/${entityType}/assignations`,
      attachDetachPayload,
    );
  }

  private getOrganizationSectorsPath(organizationCode: number | undefined) {
    return `${organizationService.getOrganizationPath(organizationCode)}/sectors`;
  }
}

export default new SectorService();
